import { useCallback, useEffect, useState } from "react";
import {
  useFieldArray,
  UseFieldArrayReturn,
  UseFormReturn,
} from "react-hook-form";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useWebSocket from "react-use-websocket";
import {
  AdminNonPartisanChartFormValues,
  useAdminNonPartisanChartForm,
} from "../../../../forms/charts/admin/admin-chart-non-partisan-form";
import { ChartTypeEnum } from "../../../../models/chart";
import { State } from "../../../../models/state";
import { ChartsService } from "../../../../services/charts-service";
import { CognitoService } from "../../../../services/cognito-service";
import { StateService } from "../../../../services/state-services";

export interface AdminManageNonPartisanChartState {
  loading: boolean;
  form: UseFormReturn<AdminNonPartisanChartFormValues>;
  racesFieldArray: UseFieldArrayReturn<
    AdminNonPartisanChartFormValues,
    "races"
  >;
  raceTypes: State[];
  saveAndContinueDialog: boolean;
  states: State[];
  saving: boolean;
  chartId: string;
  showFileUpload: boolean;

  useExistingBallotFile: () => void;
  uploadNewBallotFile: () => void;
  showAlreadyBallotFileDialog: boolean;
  openSaveAndContinueDialog: () => void;
  closeSaveAndContinueDialog: () => void;
  addNewRace: () => void;
  removeRace: (index: number) => void;
  moveRace: (dragIndex: number, hoverIndex: number) => void;
  cancel: () => void;
  saveAndExit: () => void;
  saveAndContinue: (action: "full-deck" | "chart") => void;
  cancelFileUpload: () => void;
}

const useAdminManageNonPartisanChart = (
  chartType: ChartTypeEnum
): AdminManageNonPartisanChartState => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [chartId, setChartId] = useState<string>("");
  const [raceTypes, setRaceTypes] = useState<State[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [shouldWebSocketConnect, setShouldWebSocketConnect] = useState(false);
  const [chartHasBallotFile, setChartHasBallotFile] = useState(false);
  const [showAlreadyBallotFileDialog, setShowAlreadyBallotFileDialog] =
    useState(false);
  const [token, setToken] = useState<string>("");
  const [dialogAction, setDialogAction] = useState<"full-deck" | "chart">(
    "chart"
  );
  const [showFileUpload, setShowFileUpload] = useState<boolean>(false);
  const [gotFirstWebSocketMessage, setGotFirstWebSocketMessage] =
    useState<boolean>(false);

  const [saveAndContinueDialog, setSaveAndContinueDialog] =
    useState<boolean>(false);

  const params = useParams<{ chartId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const form = useAdminNonPartisanChartForm();

  const racesFieldArray = useFieldArray({
    control: form.control,
    name: "races",
  });

  useEffect(() => {
    (async () => {
      const _raceTypes = await ChartsService.getRaceTypes();
      setRaceTypes(_raceTypes);
      const _states = await StateService.getActiveStatesByElectionType(
        chartType
      );
      setStates(_states);

      if (params.chartId) {
        const isEditing = !location.pathname.includes("duplicate");
        setChartId(params.chartId);
        const chart = await ChartsService.getChart(params.chartId);
        if (isEditing) setChartHasBallotFile(Boolean(chart.ballotFile));
        form.reset({
          municipality: chart.municipality,
          county: chart.county,
          precinct: isEditing ? chart.precinct : "",
          electionDate: getDateCleanedForLoading(chart.electionDate),
          showCandidateNames: chart.showCandidateNames,
          state: _states
            .find((s) => s.name === chart.state.name)
            ?.id.toString(),

          races: chart.races.map((race) => ({
            id: race.id,
            name: race.name,
            type: race.type,
            voteFor: race.voteFor,
            writeIns: race.writeIns,
            candidates: race.candidates,
            candidateNames: race.candidateNames.map((item) => ({ name: item.name, order: item.order })),
            raceType: race.raceType.name,
          })),
        });
      }

      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getWebsocketUrl = useCallback(() => {
    return `${process.env.REACT_APP_SERVICE_URL?.replace(
      "http://",
      "ws://"
    ).replace("https://", "wss://")}/charts/admin/process/${chartId}`;
  }, [chartId]);

  useWebSocket(
    getWebsocketUrl,
    {
      share: false,
      shouldReconnect: (closeEvent) => false,
      onClose: (event: WebSocketEventMap["close"]) => {
        setSaving(false);
        setToken("");
        return;
      },
      onMessage: (event: WebSocketEventMap["message"]) => {
        if (!gotFirstWebSocketMessage) {
          toast.remove();
          toast.success("Generating Chart Files");
          setGotFirstWebSocketMessage(true);
        }
        console.log("message", event.data);
        if (event.data.includes("FINISHED")) {
          toast.remove();
          // set should connect to false
          setShouldWebSocketConnect(false);
          setToken("");
          setSaving(false);
          if (dialogAction === "full-deck") {
            if (chartHasBallotFile) setShowAlreadyBallotFileDialog(true);
            else setShowFileUpload(true);
          } else {
            navigate(`/elections/non-partisan?status=Completed`);
          }
        } else if (event.data.includes("ERROR")) {
          // set should connect to false
          setShouldWebSocketConnect(false);
          toast.error("Error occurred while generating the chart files.");
          setToken("");
          setSaving(false);
        }
      },
      onError: (event: WebSocketEventMap["error"]) => {
        setShouldWebSocketConnect(false);
        toast.error("Error occurred while generating the chart files.");
        setToken("");
        setSaving(false);
      },
      protocols: ["Authorization", token],
    },
    shouldWebSocketConnect
  );

  const onSubmit = async (
    values: AdminNonPartisanChartFormValues,
    generateChart: boolean
  ): Promise<string> => {
    const isEditing = !location.pathname.includes("duplicate") && chartId;
    const payload = {
      ...(isEditing ? { id: chartId } : {}),
      adminchart: false,
      county: values.county.includes('/') ? values.county.replace('/', '-') :
        values.county.includes('\\') ? values.county.replace('\\', '-') : values.county,
      electionTypeId: chartType,
      electiondate: `${values.electionDate}T00:00:00Z`,
      labelsonright: false,
      municipality: values.municipality.includes('/') ? values.municipality.replace('/', '-') :
        values.municipality.includes('\\') ? values.municipality.replace('\\', '-') : values.municipality,
      precinct: values.precinct.includes('/') ? values.precinct.replace('/', '-') :
        values.precinct.includes('\\') ? values.precinct.replace('\\', '-') : values.precinct,
      stateId: parseInt(values.state),
      showCandidateNames: values.showCandidateNames,

      races: values.races.map((race, raceIdx) => {
        return {
          ...(isEditing ? { id: race!.id as string } : {}),
          marks: [],
          name: race.name,
          order: raceIdx,
          type: race.type,
          raceTypeId: 1,
          voteFor: race.type.toLowerCase() === "proposal" ? 1 : race.voteFor,
          writeIns: race.type.toLowerCase() === "proposal" ? 0 : race.writeIns,
          candidates:
            race.type.toLowerCase() === "proposal"
              ? 0
              : race.candidates < 0
                ? 0
                : race.candidates,
          candidateNames: race.candidateNames ? (values.showCandidateNames ? race.candidateNames.map(item => ({ ...item, order: +item.order })).filter(Boolean) : []) : [],
          politicalParties: [],
        };
      }),
    };


    let returnedId = "";
    if (isEditing) {
      // @ts-ignore
      const val = await ChartsService.adminEditChart(payload);
      returnedId = val.id;
    } else {
      // @ts-ignore
      const val = await ChartsService.adminCreateChart(payload);
      returnedId = val.id;
    }

    if (generateChart && returnedId !== '') {
      console.log("generating chart");
      // this process may take a bit, so we have to websockets to make sure we stay connected
      const session = await CognitoService.getSession();
      if (session) {
        setToken(session.getIdToken().getJwtToken());
      }
      setChartId(returnedId);
      setShouldWebSocketConnect(true);
    }

    return returnedId;
  };

  const addNewRace = () => {
    form.clearErrors("races");
    const lastRace = form.getValues().races
      ? form.getValues().races[
      form.getValues().races ? form.getValues().races.length - 1 : 0
      ]
      : undefined;
    racesFieldArray.append({
      name: "",
      voteFor: 1,
      writeIns: 1,
      candidates: 0,
      candidateNames: [],
      type: lastRace ? lastRace.type : "Race",
      raceType: lastRace ? lastRace.raceType : "Non-Partisan",
    });
  };

  const removeRace = (index: number) => racesFieldArray.remove(index);

  const moveRace = (dragIndex: number, hoverIndex: number) => {
    const { races } = form.getValues();
    const dragCard = races[dragIndex];
    races.splice(dragIndex, 1);
    races.splice(hoverIndex, 0, dragCard);
    form.setValue("races", [...races]);
  };

  const cancel = () => {
    navigate(-1);
  };

  const saveAndExit = async () => {
    try {
      await toast.promise(
        form.handleSubmit(
          (values) => onSubmit(values, false),
          () => {
            // eslint-disable-next-line no-throw-literal
            throw "Form is not valid";
          }
        )(),
        {
          loading: "Saving...",
          success: () => {
            navigate(-1);
            return <b>Successful chart save!</b>;
          },
          error: (e) => {
            return e
              ? e
              : e.message ?? e ?? "An Error Occurred.";
          },
        },
        { duration: 5000 }
      );
    } catch (e) {
      console.log(e);
    } finally {
      setSaving(false);
    }
  };

  const saveAndContinue = async (action: "full-deck" | "chart") => {
    try {
      setSaving(true);
      setDialogAction(action);
      await toast.promise(
        form.handleSubmit(
          (values) => {
            onSubmit(values, true).catch((e) => {
              toast.error(
                e?.response?.data
                  ? e.response.data
                  : e.message ?? "An Error Occurred."
              );
            });
          },
          () => {
            toast.error("Form is not valid");
          }
        )(),
        {
          loading: "Saving...",
          success: () => {
            setSaveAndContinueDialog(false);
            return <b>Successful chart save!</b>;
          },
          error: (e: any) => {
            return e?.response?.data
              ? e.response.data
              : e.message ?? e ?? "An Error Occurred.";
          },
        },
        { duration: 5000 }
      );
    } catch (e) {
      console.log(e);
      setSaving(false);
    }
  };

  const cancelFileUpload = () => {
    setShowFileUpload(false);
    navigate("/elections/non-partisan?status=Completed");
  };

  const useExistingBallotFile = () => {
    navigate(`/mark-ballot/${chartId}`);
  };

  const uploadNewBallotFile = () => {
    setShowAlreadyBallotFileDialog(false);
    setShowFileUpload(true);
  };

  const getDateCleanedForLoading = (date: string): string => {
    const d = new Date(date);
    console.log(date);
    const temp = `${d.getUTCFullYear()}-${d.getUTCMonth() + 1 < 10
      ? "0" + (d.getUTCMonth() + 1)
      : d.getUTCMonth() + 1
      }-${d.getUTCDate() < 10 ? "0" + d.getUTCDate() : d.getUTCDate()}`;
    console.log(temp);
    return temp;
  };

  return {
    loading,
    form,
    raceTypes,
    racesFieldArray,
    saveAndContinueDialog,
    states,
    saving,
    chartId,
    showFileUpload,
    showAlreadyBallotFileDialog,

    useExistingBallotFile,
    uploadNewBallotFile,
    closeSaveAndContinueDialog: () => setSaveAndContinueDialog(false),
    openSaveAndContinueDialog: () => setSaveAndContinueDialog(true),
    addNewRace,
    removeRace,
    moveRace,
    cancel,
    saveAndExit,
    saveAndContinue,
    cancelFileUpload,
  };
};

export default useAdminManageNonPartisanChart;
